'use client'

import { useEffect, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { CheckmarkCircle01Icon, CancelCircleIcon, Loading02Icon } from '@hugeicons-pro/core-solid-rounded'
import { createClient } from '@/utils/supabase/client'
import { cn } from '@/lib/utils'

type SupplyConfigWriteStatus = 'pending' | 'claimed' | 'succeeded' | 'failed' | 'dead'

type SupplyConfigWrite = {
  id: string
  status: SupplyConfigWriteStatus
  attempts: number | null
  last_error: string | null
  filtered_rejections: Record<string, unknown> | null
}

function filteredRejectionEntries(write: SupplyConfigWrite | null): string[] {
  const filtered = write?.filtered_rejections
  if (!filtered || typeof filtered !== 'object') return []

  return ['domains', 'bundleIds', 'appNames'].flatMap((key) => {
    const value = filtered[key]
    return Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0) : []
  })
}

function cappedDetails(entries: string[]): string {
  const visible = entries.slice(0, 5)
  const suffix = entries.length > visible.length ? ` and ${entries.length - visible.length} more` : ''
  const detail = `${visible.join(', ')}${suffix}`
  return detail.length > 80 ? `${detail.slice(0, 77)}...` : detail
}

type SupplyConfigWritePillProps = {
  queueId: string
  /**
   * `card` preserves the original spacing inside tool results. `message`
   * uses tighter padding for the always-visible assistant message row.
   */
  placement?: 'message' | 'card'
}

function tone(status: SupplyConfigWriteStatus): string {
  if (status === 'succeeded') return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  if (status === 'dead' || status === 'failed') return 'border-red-200 bg-red-50 text-red-700'
  return 'border-atrium-border bg-[#F3F2F1] text-[#5F5E61]'
}

function content(write: SupplyConfigWrite | null) {
  const status = write?.status ?? 'pending'
  const attempts = write?.attempts ?? 0

  if (status === 'succeeded') {
    const skipped = filteredRejectionEntries(write)
    if (skipped.length > 0) {
      return {
        icon: CheckmarkCircle01Icon,
        label: `Synced - ${skipped.length} entries skipped (invalid): ${cappedDetails(skipped)}`,
        title: skipped.join(', '),
        spinning: false,
      }
    }

    const totalAttempts = attempts + 1
    return {
      icon: CheckmarkCircle01Icon,
      label: totalAttempts > 1 ? `Synced - updated after ${totalAttempts} attempts` : 'Synced',
      title: undefined,
      spinning: false,
    }
  }

  if (status === 'dead' || status === 'failed') {
    return {
      icon: CancelCircleIcon,
      label: `Failed after ${attempts} attempts${write?.last_error ? `: ${write.last_error}` : ''}`,
      title: undefined,
      spinning: false,
    }
  }

  return {
    icon: Loading02Icon,
    label: 'Syncing to ad network...',
    title: undefined,
    spinning: true,
  }
}

export function SupplyConfigWritePill({ queueId, placement = 'card' }: SupplyConfigWritePillProps) {
  const [write, setWrite] = useState<SupplyConfigWrite | null>(null)

  useEffect(() => {
    const supabase = createClient()
    let mounted = true

    const load = async () => {
      const { data } = await supabase
        .from('supply_config_writes')
        .select('id, status, attempts, last_error, filtered_rejections')
        .eq('id', queueId)
        .single()

      if (mounted && data) setWrite(data as SupplyConfigWrite)
    }

    void load()

    const channel = supabase
      .channel(`supply_config_writes:${queueId}`)
      .on(
        'postgres_changes' as any,
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'supply_config_writes',
          filter: `id=eq.${queueId}`,
        },
        (payload: any) => {
          setWrite(payload.new as SupplyConfigWrite)
        },
      )
      .subscribe()

    return () => {
      mounted = false
      void supabase.removeChannel(channel)
    }
  }, [queueId])

  const status = write?.status ?? 'pending'
  const display = content(write)

  return (
    <div
      className={cn(
        'inline-flex max-w-full items-center gap-2 rounded-full border text-xs font-medium',
        placement === 'message' ? 'mt-2 px-2.5 py-1' : 'mt-3 px-3 py-1.5',
        tone(status),
      )}
      title={display.title}
    >
      <HugeiconsIcon
        icon={display.icon}
        className={cn('size-3.5 shrink-0', display.spinning && 'animate-spin')}
      />
      <span className="min-w-0 truncate">{display.label}</span>
    </div>
  )
}
